import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface PageHeaderActionsContextValue {
  slot: HTMLElement | null;
  setSlot: (slot: HTMLElement | null) => void;
}

const PageHeaderActionsContext =
  createContext<PageHeaderActionsContextValue | null>(null);

/**
 * Lets a page body render its primary action inside the PageHeader action row
 * without lifting dialog state out of the table component.
 */
export function PageHeaderActionsProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);

  return (
    <PageHeaderActionsContext.Provider value={value}>
      {children}
    </PageHeaderActionsContext.Provider>
  );
}

export function usePageHeaderActionsContext(): PageHeaderActionsContextValue | null {
  return useContext(PageHeaderActionsContext);
}

export function PageHeaderActionsSlot() {
  const context = usePageHeaderActionsContext();
  if (!context) return null;
  return <div ref={context.setSlot} className="flex items-center gap-2" />;
}

export function PageHeaderAction({ children }: { children: ReactNode }) {
  const context = usePageHeaderActionsContext();
  if (!context?.slot) return null;
  return createPortal(children, context.slot);
}
